import { Rectangle } from '../../../types/game.types';
import { Player } from '../entities/Player';
import { Enemy } from '../entities/Enemy';
import { Bullet, BulletType } from '../entities/Bullet';

export class CollisionSystem {
  
  public static checkCollision(rect1: Rectangle, rect2: Rectangle): boolean {
    return rect1.x < rect2.x + rect2.width &&
           rect1.x + rect1.width > rect2.x &&
           rect1.y < rect2.y + rect2.height &&
           rect1.y + rect1.height > rect2.y;
  }

  public static checkBulletEnemyCollisions(bullets: Bullet[], enemies: Enemy[]): Array<{bullet: Bullet, enemy: Enemy}> {
    const collisions: Array<{bullet: Bullet, enemy: Enemy}> = [];
    
    bullets.forEach(bullet => {
      if (!bullet.active || bullet.getType() !== BulletType.PLAYER) return;
      
      enemies.forEach(enemy => {
        if (!enemy.active) return;
        
        if (this.checkCollision(bullet.getBounds(), enemy.getBounds())) {
          collisions.push({ bullet, enemy });
        }
      });
    });
    
    return collisions;
  }

  public static checkBulletPlayerCollisions(bullets: Bullet[], player: Player): Bullet[] {
    const collisions: Bullet[] = [];
    
    bullets.forEach(bullet => {
      if (!bullet.active || bullet.getType() !== BulletType.ENEMY || !player.active) return;
      
      if (this.checkCollision(bullet.getBounds(), player.getBounds())) {
        collisions.push(bullet);
      }
    });
    
    return collisions;
  }

  public static checkEnemyPlayerCollisions(enemies: Enemy[], player: Player): Enemy[] {
    const collisions: Enemy[] = [];
    
    if (!player.active) return collisions;
    
    enemies.forEach(enemy => {
      if (!enemy.active) return;
      
      if (this.checkCollision(enemy.getBounds(), player.getBounds())) {
        collisions.push(enemy);
      }
    });
    
    return collisions;
  }

  public static checkBulletBulletCollisions(bullets: Bullet[]): Array<{bullet1: Bullet, bullet2: Bullet}> {
    const collisions: Array<{bullet1: Bullet, bullet2: Bullet}> = [];
    
    for (let i = 0; i < bullets.length; i++) {
      const bullet1 = bullets[i];
      if (!bullet1.active) continue;
      
      for (let j = i + 1; j < bullets.length; j++) {
        const bullet2 = bullets[j];
        if (!bullet2.active) continue;
        
        // Only check collisions between different bullet types
        if (bullet1.getType() !== bullet2.getType()) {
          if (this.checkCollision(bullet1.getBounds(), bullet2.getBounds())) {
            collisions.push({ bullet1, bullet2 });
          }
        }
      }
    }
    
    return collisions;
  }

  // Utility method to check if a rectangle is within bounds
  public static isWithinBounds(rect: Rectangle, bounds: Rectangle): boolean {
    return rect.x >= bounds.x &&
           rect.y >= bounds.y &&
           rect.x + rect.width <= bounds.x + bounds.width &&
           rect.y + rect.height <= bounds.y + bounds.height;
  }

  // Utility method to check if a point is inside a rectangle
  public static pointInRect(x: number, y: number, rect: Rectangle): boolean {
    return x >= rect.x &&
           x <= rect.x + rect.width &&
           y >= rect.y &&
           y <= rect.y + rect.height;
  }

  // Advanced collision detection for pixel-perfect collisions (if needed)
  public static pixelPerfectCollision(
    rect1: Rectangle, 
    rect2: Rectangle, 
    mask1?: boolean[][], 
    mask2?: boolean[][]
  ): boolean {
    // First check bounding box collision
    if (!this.checkCollision(rect1, rect2)) {
      return false;
    }
    
    // If no masks provided, return bounding box result
    if (!mask1 || !mask2) {
      return true;
    }
    
    // Calculate overlap area
    const overlapX = Math.max(rect1.x, rect2.x);
    const overlapY = Math.max(rect1.y, rect2.y);
    const overlapWidth = Math.min(rect1.x + rect1.width, rect2.x + rect2.width) - overlapX;
    const overlapHeight = Math.min(rect1.y + rect1.height, rect2.y + rect2.height) - overlapY;
    
    // Check pixel collision in overlap area
    for (let y = 0; y < overlapHeight; y++) {
      for (let x = 0; x < overlapWidth; x++) {
        const x1 = Math.floor((overlapX + x - rect1.x) * mask1[0].length / rect1.width);
        const y1 = Math.floor((overlapY + y - rect1.y) * mask1.length / rect1.height);
        const x2 = Math.floor((overlapX + x - rect2.x) * mask2[0].length / rect2.width);
        const y2 = Math.floor((overlapY + y - rect2.y) * mask2.length / rect2.height);
        
        if (x1 >= 0 && x1 < mask1[0].length && 
            y1 >= 0 && y1 < mask1.length &&
            x2 >= 0 && x2 < mask2[0].length && 
            y2 >= 0 && y2 < mask2.length) {
          
          if (mask1[y1][x1] && mask2[y2][x2]) {
            return true;
          }
        }
      }
    }
    
    return false;
  }
}